/*
===============================================================================================================
                                 Copyright 2025-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable no-console */
import { merge } from 'lodash';
import defaultConfigContent from '@/public/portal-config/RETURNS_PORTAL_ADMIN_V2_DEFAULT.json';
import apiClient from '@/utilities/api-client';
import { getCarrierSupportedCountries, getCountries } from '@/utilities/countries';
import { applyE2EDemoFeatures } from '../E2E_DEMO/merchant-config-features';
import countries from './content/countries.json';

/**
 * Merchant Config API
 * LocalModeEnabled: If this is set to true then it uses local Config files in the project. This is set as an env var locally only
 */
export const getMerchantConfig = async ({
  merchant,
  localModeEnabled,
  hostId,
  consumerUrl,
}) => {
  let merchantConfig = {};
  let applicationConfig = {};
  let localConfig = {};
  let productName;

  if (merchant) {
    try {
      const merchantConfigData = await apiClient.get(`${hostId}/config/${localModeEnabled ? 'MERCHANT_LOCAL' : 'MERCHANT'}/companyId/${merchant}/carrierName`);
      merchantConfig = merchantConfigData.data.resource?.config;
      // PRODUCT_NAME and rootOrgId is taken from the Service Layer/ MongoDB version of the config record and not Locally in local mode
      productName = merchantConfig?.carrier?.productName;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Unauthorized');
      }
      if (error.response?.status === 400) {
        throw new Error('Bad Request');
      }
      if (error.response?.status === 404) {
        throw new Error('Merchant Not Found');
      }
      if (error.response?.status === 403) {
        throw new Error('Forbidden');
      }
    }

    if (!productName) {
      throw new Error('Merchant Not Found');
    }

    if (localModeEnabled) {
      try {
        const localConfigContent = await import(`@/public/portal-config/MERCHANT_${productName}.json`);

        localConfig = merge(defaultConfigContent, localConfigContent.default);
      } catch (error) {
        console.log(`Local Portal config error: ${error}`);
      }
    }

    if (productName) {
      try {
        const applicationConfigContent = await apiClient.get(`${hostId}/assets/company/${merchant}/config?ts=${Date.now()}`,
          {
            headers: {
              'cache-control': 'no-cache',
            },
          });

        applicationConfig = applicationConfigContent?.data;
      } catch (error) {
        console.log(`Merchant application config error: ${error}`);
      }
    }

    // This will be moved to a seperate service layer endpoint in further tickets
    if (productName) {
      try {
        const emailPreviews = await import(`@/public/email-previews/${productName}.json`);
        merchantConfig.emailPreviews = emailPreviews.default;
        if (merchantConfig?.features?.international?.enabled || localConfig?.features?.international?.enabled) {
          const emailPreviewsInternational = await import(`@/public/email-previews/international/${productName}.json`);
          merchantConfig.emailPreviewsInternational = emailPreviewsInternational.default;
        }
      } catch (error) {
        console.log(`Email previews error: ${error}`);
      }
    }

    // DEMO: This optional code is for Tim's RPS demo, and causes those demo features to show.
    // This should be removed as soon as we no longer need it
    if (merchant === 'CHALOUB_DEMO') {
      merchantConfig = applyE2EDemoFeatures(merchantConfig);
    }
  }

  const returnObject = {
    ...merchantConfig,
    applicationConfig: applicationConfig || [],
    ...(localModeEnabled && { ...localConfig }),
    name: merchant,
    accountNumber: 456,
    consumerUrl,
    emailPreviews: merchantConfig.emailPreviews,
    emailPreviewsInternational: merchantConfig.emailPreviewsInternational,
    countriesEnabled: getCountries({
      supportedCountryCodesInConfig: merchantConfig?.features?.international?.enabledCountryCodes,
      enabledCountriesInCompany: merchantConfig?.countriesEnabled,
      countriesData: countries,
    }),
    supportedCountries: getCarrierSupportedCountries(merchantConfig?.carrierCountriesEnabled, countries),
  };

  return {
    data: returnObject,
  };
};

export default getMerchantConfig;
